import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import transportService from '../../services/transportService';
import { FormSelect, FormInput } from '../../components/finance';
import { Plus, Eye, X, ArrowLeft } from '@phosphor-icons/react';
import FeeRecordForm from '../../components/transport/FeeRecordForm';
import './transport.css';

function TransportFeesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fees, setFees] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedFee, setSelectedFee] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailFee, setDetailFee] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [filters, setFilters] = useState({ payment_status: '' });
  const [pagination, setPagination] = useState({ skip: 0, limit: 50 });

  useEffect(() => {
    if (user && user.role !== 'super_admin' && user.role !== 'admin' && user.role !== 'school_admin') {
      toast.error('Access denied');
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchFees = async () => {
      try {
        setLoading(true);
        const data = await transportService.listFees(pagination);
        setFees(Array.isArray(data) ? data : data.fees || []);
      } catch (error) {
        toast.error('Failed to load fees: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchFees();
  }, [pagination]);

  const handleCreate = async (formData) => {
    try {
      await transportService.createFee(formData);
      toast.success('Fee record created successfully');
      setShowModal(false);
      setSelectedFee(null);
      const data = await transportService.listFees(pagination);
      setFees(Array.isArray(data) ? data : data.fees || []);
    } catch (error) {
      toast.error('Failed to create fee: ' + error.message);
    }
  };

  const handleUpdate = async (id, formData) => {
    try {
      await transportService.updateFee(id, formData);
      toast.success('Fee updated successfully');
      setShowModal(false);
      setSelectedFee(null);
      const data = await transportService.listFees(pagination);
      setFees(Array.isArray(data) ? data : data.fees || []);
    } catch (error) {
      toast.error('Failed to update fee: ' + error.message);
    }
  };

  const handleView = (fee) => {
    setDetailFee(fee);
    setShowDetailModal(true);
  };

  const unpaidFees = fees.filter(f => !f.is_paid);
  const paidFees = fees.filter(f => f.is_paid);
  const totalOutstanding = unpaidFees.reduce((sum, f) => sum + (f.amount_due - f.amount_paid), 0);
  const totalCollected = paidFees.reduce((sum, f) => sum + f.amount_paid, 0);

  const displayFees = activeTab === 'paid' ? paidFees : activeTab === 'unpaid' ? unpaidFees : fees;

  if (loading) {
    return <Layout><div className="loading">Loading fees...</div></Layout>;
  }

  return (
    <Layout>
      <div className="page fees-management">
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn-icon" onClick={() => navigate('/transport')} title="Back to Transport">
              <ArrowLeft size={20} weight="bold" />
            </button>
            <div>
              <h1>Transport Fees Management</h1>
              <p>Manage and collect transport fees</p>
            </div>
          </div>
          <button className="btn btn-primary" onClick={() => { setSelectedFee(null); setShowModal(true); }}>
            <Plus size={18} /> Add Fee Record
          </button>
        </div>

        <div className="summary-cards">
          <div className="summary-card">
            <h3>Total Collected</h3>
            <p className="amount">GHS {totalCollected.toFixed(2)}</p>
          </div>
          <div className="summary-card">
            <h3>Outstanding</h3>
            <p className="amount alert">{unpaidFees.length} records</p>
          </div>
          <div className="summary-card">
            <h3>Total Outstanding Amount</h3>
            <p className="amount">GHS {totalOutstanding.toFixed(2)}</p>
          </div>
        </div>

        <div className="tabs-container">
          <button
            className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All ({fees.length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'paid' ? 'active' : ''}`}
            onClick={() => setActiveTab('paid')}
          >
            Paid ({paidFees.length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'unpaid' ? 'active' : ''}`}
            onClick={() => setActiveTab('unpaid')}
          >
            Unpaid ({unpaidFees.length})
          </button>
        </div>

        <div className="table-section">
          {displayFees.length === 0 ? (
            <div className="empty-state">
              <p>No fees found</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Route</th>
                  <th>Fee Type</th>
                  <th>Amount Due</th>
                  <th>Paid</th>
                  <th>Balance</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayFees.map(fee => (
                  <tr key={fee.id}>
                    <td><strong>{fee.student?.first_name} {fee.student?.last_name}</strong></td>
                    <td>
                      <div>{fee.route?.route_name}</div>
                      <small style={{ color: '#999' }}>Fee: GHS {(fee.route?.fee_amount || 0).toFixed(2)}</small>
                    </td>
                    <td>{fee.fee_type}</td>
                    <td>GHS {fee.amount_due.toFixed(2)}</td>
                    <td>GHS {fee.amount_paid.toFixed(2)}</td>
                    <td>GHS {(fee.amount_due - fee.amount_paid).toFixed(2)}</td>
                    <td>{fee.due_date || '-'}</td>
                    <td><span className={`badge ${fee.is_paid ? 'badge-success' : 'badge-danger'}`}>{fee.is_paid ? 'Paid' : 'Unpaid'}</span></td>
                    <td className="actions">
                      <button className="btn-icon" onClick={() => handleView(fee)} title="View Details">
                        <Eye size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="pagination">
          <button disabled={pagination.skip === 0} onClick={() => setPagination({ ...pagination, skip: Math.max(0, pagination.skip - pagination.limit) })}>
            Previous
          </button>
          <span>Page {Math.floor(pagination.skip / pagination.limit) + 1}</span>
          <button disabled={displayFees.length < pagination.limit} onClick={() => setPagination({ ...pagination, skip: pagination.skip + pagination.limit })}>
            Next
          </button>
        </div>

        {showModal && (
          <FeeRecordForm
            fee={selectedFee}
            onSave={selectedFee ? (id, data) => handleUpdate(id, data) : (_, data) => handleCreate(data)}
            onClose={() => { setShowModal(false); setSelectedFee(null); }}
          />
        )}

        {showDetailModal && detailFee && (
          <div className="modal-overlay">
            <div className="modal modal-lg">
              <div className="modal-header">
                <h2>Transport Fee Details</h2>
                <button onClick={() => setShowDetailModal(false)} className="btn-close">
                  <X size={20} />
                </button>
              </div>

              <div className="modal-body">
                <div className="detail-section">
                  <h3>Student Information</h3>
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>Student Name</label>
                      <p>{detailFee.student?.first_name} {detailFee.student?.last_name}</p>
                    </div>
                    <div className="detail-item">
                      <label>Student ID</label>
                      <p>{detailFee.student_id}</p>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Route Information</h3>
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>Route Name</label>
                      <p>{detailFee.route?.route_name}</p>
                    </div>
                    <div className="detail-item">
                      <label>Route Fee</label>
                      <p>GHS {(detailFee.route?.fee_amount || 0).toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Fee Details</h3>
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>Fee Type</label>
                      <p>{detailFee.fee_type}</p>
                    </div>
                    <div className="detail-item">
                      <label>Amount Due</label>
                      <p>GHS {detailFee.amount_due.toFixed(2)}</p>
                    </div>
                  </div>
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>Amount Paid</label>
                      <p>GHS {detailFee.amount_paid.toFixed(2)}</p>
                    </div>
                    <div className="detail-item">
                      <label>Discount</label>
                      <p>GHS {(detailFee.discount || 0).toFixed(2)}</p>
                    </div>
                  </div>
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>Outstanding Balance</label>
                      <p>GHS {Math.max(0, detailFee.amount_due - detailFee.amount_paid).toFixed(2)}</p>
                    </div>
                    <div className="detail-item">
                      <label>Status</label>
                      <p>
                        <span className={`badge ${detailFee.is_paid ? 'badge-success' : 'badge-danger'}`}>
                          {detailFee.is_paid ? 'Paid' : 'Unpaid'}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Payment Information</h3>
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>Due Date</label>
                      <p>{detailFee.due_date || '-'}</p>
                    </div>
                    <div className="detail-item">
                      <label>Payment Date</label>
                      <p>{detailFee.payment_date || '-'}</p>
                    </div>
                  </div>
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>Payment Method</label>
                      <p>{detailFee.payment_method || '-'}</p>
                    </div>
                    <div className="detail-item">
                      <label>Receipt Number</label>
                      <p>{detailFee.receipt_number || '-'}</p>
                    </div>
                  </div>
                </div>

                {detailFee.gl_journal_entry_id && (
                  <div className="detail-section">
                    <h3>GL Posting Information</h3>
                    <div className="detail-row">
                      <div className="detail-item">
                        <label>Journal Entry ID</label>
                        <p>{detailFee.gl_journal_entry_id}</p>
                      </div>
                      <div className="detail-item">
                        <label>Posted Date</label>
                        <p>{detailFee.gl_posted_date || '-'}</p>
                      </div>
                    </div>
                  </div>
                )}

                {detailFee.notes && (
                  <div className="detail-section">
                    <h3>Notes</h3>
                    <p>{detailFee.notes}</p>
                  </div>
                )}

                <div className="detail-section">
                  <h3>Metadata</h3>
                  <div className="detail-row">
                    <div className="detail-item">
                      <label>Created</label>
                      <p>{new Date(detailFee.created_at).toLocaleString()}</p>
                    </div>
                    <div className="detail-item">
                      <label>Updated</label>
                      <p>{new Date(detailFee.updated_at).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default TransportFeesPage;
